'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrNavigationStart } from '@/lib/pr-v2/pr-open-timing';
import type { PrV2Tab } from '@/lib/pr-v2/tabs';
import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef } from 'react';
import { prV2SummaryQueryKey } from '@/data/v2/pr/query-keys';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { resolveDocumentLoadStart, takePrNavigationStart } from '@/lib/pr-v2/pr-open-timing';
import { getPrV2ActiveFileStoreKey } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2ContentPaintStore } from '@/lib/stores/navigation/pr-v2-content-paint-store';
type PrOpenStart = ({
  navigation: 'client';
} & PrNavigationStart) | {
  navigation: 'document';
  startedAt: number;
};
function resolvePrOpenStart(path: string): PrOpenStart | null {
  const clientStart = takePrNavigationStart(path);
  if (clientStart) {
    return {
      navigation: 'client',
      ...clientStart
    };
  }
  const documentStart = resolveDocumentLoadStart(path);
  return documentStart ? {
    navigation: 'document',
    startedAt: documentStart.startedAt
  } : null;
}
type ReportPrOpenTimingOptions = {
  owner: string;
  repo: string;
  number: string;
  activeTab: PrV2Tab;
  installationId: number | null;
};

/**
 * Sends one `v2_pr_open_timing` event for a PR page opened by a recorded click or a page load: the
 * time until the page's content area painted, and until the PR's content painted. Call it from the
 * component that mounts when the page's content renders. Opens with no recorded start, such as
 * plain links or Back and Forward, report nothing.
 */
export function useReportPrOpenTiming(t0) {
  const $ = _c(16);
  const {
    owner,
    repo,
    number,
    activeTab,
    installationId
  } = t0;
  const queryClient = useQueryClient();
  const startRef = useRef(undefined);
  let t1;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t1 = getPrV2ActiveFileStoreKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const prKey = t1;
  let t2;
  if ($[4] !== activeTab || $[5] !== installationId || $[6] !== number || $[7] !== owner || $[8] !== queryClient || $[9] !== repo) {
    t2 = (start, pagePaintedAt, contentPaintedAt) => {
      const changedFiles = queryClient.getQueryData(prV2SummaryQueryKey(owner, repo, number))?.pullRequest.metadataSummary.changedFiles;
      trackClientEvent("v2_pr_open_timing", installationId?.toString() ?? null, {
        navigation: start.navigation,
        ...(start.navigation === "client" && {
          source: start.source
        }),
        tab: activeTab,
        ms_to_page: Math.round(pagePaintedAt - start.startedAt),
        ms_to_content: Math.round(contentPaintedAt - start.startedAt),
        ...(typeof changedFiles === "number" && {
          changed_files: changedFiles
        })
      });
    };
    $[4] = activeTab;
    $[5] = installationId;
    $[6] = number;
    $[7] = owner;
    $[8] = queryClient;
    $[9] = repo;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const sendTiming = useEffectEvent(t2);
  let t3;
  if ($[11] !== prKey || $[12] !== sendTiming) {
    t3 = () => {
      if (startRef.current === undefined) {
        const resolvedStart = resolvePrOpenStart(window.location.pathname);
        const revealsWindow = resolvedStart?.navigation === "client" && resolvedStart.entrance === "window-reveal";
        startRef.current = document.visibilityState === "visible" || revealsWindow ? resolvedStart : null;
      } else {
        takePrNavigationStart(window.location.pathname);
      }
      const start_0 = startRef.current;
      if (!start_0) {
        return;
      }
      let pagePaintedAt_0 = null;
      let contentPaintedAt_0 = null;
      let timeoutId;
      const sendOnceBothPainted = () => {
        if (startRef.current === null || pagePaintedAt_0 === null || contentPaintedAt_0 === null) {
          return;
        }
        startRef.current = null;
        sendTiming(start_0, pagePaintedAt_0, contentPaintedAt_0);
      };
      const frameId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          pagePaintedAt_0 = performance.now();
          sendOnceBothPainted();
        }, 0);
      });
      const readContentPaint = () => {
        const paintedAt = usePrV2ContentPaintStore.getState().paintedAtByPrKey[prKey];
        if (contentPaintedAt_0 === null && paintedAt !== undefined && paintedAt >= start_0.startedAt) {
          contentPaintedAt_0 = paintedAt;
          sendOnceBothPainted();
        }
      };
      const unsubscribeFromContentPaint = usePrV2ContentPaintStore.subscribe(readContentPaint);
      readContentPaint();
      const handleVisibilityChange = () => {
        if (document.visibilityState !== "visible") {
          startRef.current = null;
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        window.cancelAnimationFrame(frameId);
        window.clearTimeout(timeoutId);
        unsubscribeFromContentPaint();
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        if (pagePaintedAt_0 !== null) {
          startRef.current = null;
        }
      };
    };
    $[11] = prKey;
    $[12] = sendTiming;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== prKey) {
    t4 = [prKey];
    $[14] = prKey;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useEffect(t3, t4);
}